import { forwardRef } from 'react';
import MuiBox from '@mui/material/Box';
import MuiStack from '@mui/material/Stack';
import MuiContainer from '@mui/material/Container';
import MuiCard from '@mui/material/Card';
import MuiCardContent from '@mui/material/CardContent';
import MuiDivider from '@mui/material/Divider';
import { layout } from '../theme/tokens';

/**
 * Layout primitives, all of them Material UI underneath.
 *
 * MUI's Box renders whatever its `component` prop names, which is how every
 * structural HTML element in the application is produced without any file
 * writing a tag. The `as` prop here is restricted to a fixed list of layout and
 * sectioning elements: enough to write correct document structure, not enough
 * to smuggle an <input> or an <a> past the components that give those their
 * behaviour. Anything unrecognised falls back to a div rather than throwing,
 * because a wrong tag should not blank the screen.
 *
 * Spacing is given in theme units (theme.spacing(1) === 4px), never pixels, so
 * a call site cannot introduce a value off the rhythm.
 */

const ALLOWED_ELEMENTS = new Set([
  'div',
  'section',
  'article',
  'header',
  'footer',
  'nav',
  'aside',
  'main',
  'figure',
  'dl',
  'dt',
  'dd',
  'fieldset',
  'legend'
]);

export const Box = forwardRef(function Box({ as = 'div', padding, children, ...rest }, ref) {
  const component = ALLOWED_ELEMENTS.has(as) ? as : 'div';

  return (
    <MuiBox ref={ref} component={component} p={padding} {...rest}>
      {children}
    </MuiBox>
  );
});

/**
 * Vertical arrangement. `gap` is in theme spacing units.
 *
 * Alignment goes through `sx`, not through top-level props. Material UI's Stack
 * takes only direction/spacing/divider/useFlexGap — it does not forward the
 * layout system props, so an `alignItems` passed directly lands on the DOM node
 * as an unknown attribute and silently does nothing.
 */
export const Stack = forwardRef(function Stack({ gap = 4, align, justify, children, sx, ...rest }, ref) {
  return (
    <MuiStack
      ref={ref}
      direction="column"
      spacing={gap}
      sx={{ alignItems: align, justifyContent: justify, ...sx }}
      {...rest}
    >
      {children}
    </MuiStack>
  );
});

/** Horizontal arrangement that wraps when it runs out of room. */
export const Inline = forwardRef(function Inline(
  { gap = 2, align = 'center', justify, wrap = true, children, sx, ...rest },
  ref
) {
  return (
    <MuiStack
      ref={ref}
      direction="row"
      spacing={gap}
      // useFlexGap so the gap survives wrapping — MUI's default margin-based
      // spacing leaves a stray offset on the first item of each new row.
      useFlexGap
      sx={{
        alignItems: align,
        justifyContent: justify,
        flexWrap: wrap ? 'wrap' : 'nowrap',
        ...sx
      }}
      {...rest}
    >
      {children}
    </MuiStack>
  );
});

/**
 * A grid that fits as many columns as will hold `min`, then reflows.
 *
 * Built on Box rather than MUI's Grid because Grid asks each child to declare
 * its own span. Here the layout is decided by available width alone, which is
 * what a list of equally-important cards wants, and it behaves the same in a
 * sidebar as at full width.
 */
export const Grid = forwardRef(function Grid({ gap = 4, min = 220, children, sx, ...rest }, ref) {
  return (
    <MuiBox
      ref={ref}
      sx={{
        display: 'grid',
        gap,
        gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))`,
        ...sx
      }}
      {...rest}
    >
      {children}
    </MuiBox>
  );
});

/**
 * Two columns side by side on a wide screen, stacked on a narrow one.
 *
 * `ratio` is a CSS grid template applied from the `md` breakpoint up; below it
 * the columns become rows. Expressed in fractions rather than percentages so
 * the gap is taken out of the available space rather than causing an overflow.
 *
 * Used where two panels genuinely belong together — a headline figure and the
 * chart that explains it — rather than as a general page-splitting device. Two
 * unrelated things side by side just make each one narrower.
 */
export const Split = forwardRef(function Split(
  { ratio = '1fr 2fr', gap = 6, align = 'stretch', children, sx, ...rest },
  ref
) {
  return (
    <MuiBox
      ref={ref}
      sx={{
        display: 'grid',
        gap,
        alignItems: align,
        gridTemplateColumns: { xs: '1fr', md: ratio },
        ...sx
      }}
      {...rest}
    >
      {children}
    </MuiBox>
  );
});

/** Centres page content and caps its width. Every page body sits in one. */
export const Container = forwardRef(function Container({ children, ...rest }, ref) {
  return (
    <MuiContainer ref={ref} maxWidth={layout.contentMaxWidth} {...rest}>
      {children}
    </MuiContainer>
  );
});

/**
 * A bordered panel.
 *
 * Wraps its children in CardContent so callers get the right internal padding
 * without knowing MUI's two-part Card/CardContent structure.
 */
export const Card = forwardRef(function Card({ padding = 6, children, ...rest }, ref) {
  return (
    <MuiCard ref={ref} {...rest}>
      <MuiCardContent sx={{ p: padding, '&:last-child': { pb: padding } }}>{children}</MuiCardContent>
    </MuiCard>
  );
});

/* Sectioning wrappers, so a page can express document structure without naming
   an HTML element at the call site. */

export const PageHeader = forwardRef(function PageHeader(props, ref) {
  return <Box ref={ref} as="header" {...props} />;
});

export const PageMain = forwardRef(function PageMain(props, ref) {
  return <Box ref={ref} as="main" {...props} />;
});

export const PageFooter = forwardRef(function PageFooter(props, ref) {
  return <Box ref={ref} as="footer" {...props} />;
});

export const Nav = forwardRef(function Nav(props, ref) {
  return <Box ref={ref} as="nav" {...props} />;
});

export const Divider = forwardRef(function Divider(props, ref) {
  return <MuiDivider ref={ref} {...props} />;
});

/**
 * Content for screen readers only.
 *
 * MUI's `visuallyHidden` style object is the canonical implementation of the
 * clip-rect technique; using it avoids a hand-rolled copy drifting out of date.
 */
export function VisuallyHidden({ children }) {
  return (
    <MuiBox component="span" sx={visuallyHiddenSx}>
      {children}
    </MuiBox>
  );
}

/** Exported so other primitives (the table caption) can hide content the same way. */
export const visuallyHiddenSx = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px'
};
